import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ArrowLeft, User, Mail, Phone, Camera, Save, Eye, EyeOff, 
  Linkedin, Instagram, MessageCircle, Facebook, Award,
  FileText, Briefcase, Target, Heart, Percent, Upload, Gift,
  Lightbulb, Sparkles, CheckCircle, Info, Book, Plus, Trash2, Link,
  ToggleLeft, ToggleRight, Edit2, ExternalLink, AlertTriangle, Loader2,
  CreditCard, Receipt, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente para mostrar y editar una obra en el perfil
const WorkProfileCard = ({ work, onToggleVisibility, onUpdateInfo, saving }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    public_url: work.public_url || '',
    cover_image: work.cover_image || ''
  });

  const handleSave = () => {
    onUpdateInfo(work.id, editData.public_url, editData.cover_image);
    setIsEditing(false);
  };

  return (
    <div className={`bg-black/30 border rounded-lg p-4 ${work.show_in_profile ? 'border-green-500/40' : 'border-purple-500/20'}`}>
      <div className="flex items-start gap-4">
        {/* Portada */}
        {work.cover_image ? (
          <img 
            src={work.cover_image} 
            alt={work.title}
            className="w-16 h-20 object-cover rounded-lg border border-purple-500/30"
          />
        ) : (
          <div className="w-16 h-20 bg-purple-500/20 rounded-lg flex items-center justify-center border border-purple-500/30">
            <Book className="w-8 h-8 text-purple-400" />
          </div>
        )}

        {/* Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-white font-semibold">{work.title}</h4>
            {work.formats?.length > 0 && (
              <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
                {work.formats.join(', ')}
              </span>
            )}
          </div>
          
          {work.public_url ? (
            <a 
              href={work.public_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-400 text-sm hover:text-purple-300 flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Ver en app.viubux.com
            </a>
          ) : (
            <p className="text-white/40 text-sm">Sin enlace configurado</p>
          )}
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsEditing(!isEditing)}
            variant="ghost"
            size="sm"
            className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => onToggleVisibility(work.id, work.show_in_profile)}
            disabled={saving}
            variant="ghost"
            size="sm"
            className={work.show_in_profile 
              ? "text-green-400 hover:text-green-300 hover:bg-green-500/10" 
              : "text-white/40 hover:text-white/60 hover:bg-white/10"
            }
            title={work.show_in_profile ? "Visible en perfil público" : "Oculta del perfil público"}
          >
            {work.show_in_profile ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Badge de visibilidad */}
      <div className="mt-2">
        {work.show_in_profile ? (
          <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded flex items-center gap-1 w-fit">
            <Eye className="w-3 h-3" /> Visible en perfil público
          </span>
        ) : (
          <span className="text-xs bg-white/10 text-white/50 px-2 py-1 rounded flex items-center gap-1 w-fit">
            <EyeOff className="w-3 h-3" /> Oculta del perfil
          </span>
        )}
      </div>

      {/* Formulario de edición */}
      {isEditing && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
          <div>
            <label className="block text-white/70 text-sm mb-1">URL en app.viubux.com</label>
            <input
              type="url"
              value={editData.public_url}
              onChange={(e) => setEditData({...editData, public_url: e.target.value})}
              className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              placeholder="https://app.viubux.com/libro/..."
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">URL de la Portada</label>
            <input
              type="url"
              value={editData.cover_image}
              onChange={(e) => setEditData({...editData, cover_image: e.target.value})}
              className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              placeholder="https://..."
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              <Save className="w-3 h-3 mr-1" />
              Guardar
            </Button>
            <Button
              onClick={() => setIsEditing(false)}
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const UserProfile = () => {
  const { user, token, login, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [badges, setBadges] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [showProfileGuide, setShowProfileGuide] = useState(false);
  const [billingHistory, setBillingHistory] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [subInfo, setSubInfo] = useState(null);

  const fetchBillingHistory = async () => {
    setBillingLoading(true);
    try {
      const [histRes, subRes] = await Promise.all([
        axios.get(`${API}/user/payment-history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/subscription-status`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setBillingHistory(histRes.data);
      setSubInfo(subRes.data);
    } catch (e) {
      toast.error('No se pudo cargar el historial de compras');
      setBillingHistory({ history: [], total_count: 0, total_paid_mxn: 0 });
    } finally {
      setBillingLoading(false);
    }
  };

  const [secEmail, setSecEmail] = useState('');
  const [secPassword, setSecPassword] = useState('');
  const [secSaving, setSecSaving] = useState(false);
  const [secEmailCurrent, setSecEmailCurrent] = useState(user?.secondary_email || '');
  const [secPending, setSecPending] = useState(user?.secondary_email_pending || '');

  const saveSecondaryEmail = async () => {
    if (!secEmail || !secPassword) { toast.error('Ingresa el correo y tu contraseña'); return; }
    setSecSaving(true);
    try {
      const res = await axios.post(`${API}/user/secondary-email`, { secondary_email: secEmail, password: secPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setSecPending(res.data.pending_email || secEmail);
      setSecEmail(''); setSecPassword('');
      toast.success(res.data.message || 'Te enviamos un correo de confirmación.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el correo adicional');
    } finally { setSecSaving(false); }
  };

  const removeSecondaryEmail = async () => {
    setSecSaving(true);
    try {
      await axios.delete(`${API}/user/secondary-email`, { headers: { Authorization: `Bearer ${token}` } });
      setSecEmailCurrent(''); setSecPending('');
      toast.success('Correo adicional eliminado');
    } catch (e) {
      toast.error('No se pudo eliminar');
    } finally { setSecSaving(false); }
  };
  
  const [profileData, setProfileData] = useState({
    full_name: '',
    username: '',
    phone: '',
    bio: '',
    value_proposition: '',
    life_mission: '',
    profession: '',
    community_benefit: '', // "Mi Beneficio para la Comunidad"
    community_discount: 0,
    whatsapp: '',
    linkedin: '',
    instagram: '',
    tiktok: '',
    facebook: '',
    referral_redirect_url: '',
    referral_redirect_enabled: false,
    profile_photo: null
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [fiscalData, setFiscalData] = useState({
    rfc: '',
    razon_social: '',
    csf_file: null,
    csf_filename: ''
  });

  // Danger Zone state
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Estado para obras del autor (desde sistema de regalías)
  const [authorWorks, setAuthorWorks] = useState([]);

  useEffect(() => {
    fetchProfileData();
    fetchBadges();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
      // Cargar obras del autor (desde sistema de regalías)
      if (response.data.works) {
        setAuthorWorks(response.data.works);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Use basic user data if profile endpoint doesn't exist yet
      if (user) {
        setProfileData(prev => ({
          ...prev,
          full_name: user.full_name || '',
          username: user.username || ''
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/user/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadges(response.data.badge_details || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contraseña actualizada correctamente');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFiscalData = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('rfc', fiscalData.rfc);
      formData.append('razon_social', fiscalData.razon_social);
      if (fiscalData.csf_file) {
        formData.append('csf_file', fiscalData.csf_file);
      }
      
      await axios.post(`${API}/user/fiscal-data`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Datos fiscales guardados correctamente');
    } catch (error) {
      console.error('Error saving fiscal data:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar datos fiscales');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('photo', file);
    
    try {
      const response = await axios.post(`${API}/user/upload-photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setProfileData(prev => ({ ...prev, profile_photo: response.data.photo_url }));
      toast.success('Foto actualizada');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Error al subir la foto');
    }
  };

  const handleCSFUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFiscalData(prev => ({ ...prev, csf_file: file, csf_filename: file.name }));
    }
  };

  // Danger Zone: Fetch delete preview
  const fetchDeletePreview = async () => {
    setLoadingPreview(true);
    try {
      const response = await axios.get(`${API}/user/delete-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletePreview(response.data);
    } catch (error) {
      console.error('Error fetching delete preview:', error);
      toast.error('Error loading account data');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Danger Zone: Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    if (!deletePassword) {
      toast.error('Please enter your password');
      return;
    }

    setDeleting(true);
    try {
      await axios.post(`${API}/user/delete-account`, {
        password: deletePassword,
        confirmation: deleteConfirmation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Account deleted successfully');
      setShowDeleteModal(false);
      
      // Logout and redirect
      setTimeout(() => {
        logout();
        navigate('/');
      }, 1500);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Error deleting account');
    } finally {
      setDeleting(false);
    }
  };

  // Función para alternar visibilidad de obra en perfil público
  const toggleWorkVisibility = async (workId, currentVisibility) => {
    setSaving(true);
    try {
      await axios.put(`${API}/works/${workId}`, 
        { show_in_profile: !currentVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Actualizar estado local
      setAuthorWorks(authorWorks.map(w => 
        w.id === workId ? { ...w, show_in_profile: !currentVisibility } : w
      ));
      toast.success(!currentVisibility ? 'Obra visible en perfil público' : 'Obra oculta del perfil público');
    } catch (error) {
      console.error('Error updating work:', error);
      toast.error('Error al actualizar la obra');
    } finally {
      setSaving(false);
    }
  };

  // Función para actualizar URL/portada de obra
  const updateWorkPublicInfo = async (workId, publicUrl, coverImage) => {
    setSaving(true);
    try {
      await axios.put(`${API}/works/${workId}`, 
        { public_url: publicUrl, cover_image: coverImage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Actualizar estado local
      setAuthorWorks(authorWorks.map(w => 
        w.id === workId ? { ...w, public_url: publicUrl, cover_image: coverImage } : w
      ));
      toast.success('Información de obra actualizada');
    } catch (error) {
      console.error('Error updating work:', error);
      toast.error('Error al actualizar la obra');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
        <div className="text-white">Cargando perfil...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #080c2d 0%, #1a1a3e 100%)' }}>
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-white/70 hover:text-white flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="verdana-regular">Volver al Dashboard</span>
            </button>
          </div>
          <h1 className="gotham-bold text-2xl text-white">Mi Perfil</h1>
          <div className="w-32"></div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Profile Header Card */}
          <Card className="card-viubux-dark mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Profile Photo */}
                <div className="relative">
                  <div 
                    className="w-32 h-32 rounded-full bg-purple-500/20 border-4 border-purple-500/50 flex items-center justify-center overflow-hidden cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {profileData.profile_photo ? (
                      <img 
                        src={profileData.profile_photo} 
                        alt="Foto de perfil" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-16 h-16 text-purple-400" />
                    )}
                  </div>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 bg-purple-500 p-2 rounded-full hover:bg-purple-600 transition-colors"
                  >
                    <Camera className="w-4 h-4 text-white" />
                  </button>
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handlePhotoUpload}
                  />
                </div>
                
                {/* User Info */}
                <div className="flex-1 text-center md:text-left">
                  <h2 className="gotham-bold text-2xl text-white">{profileData.full_name || user?.full_name}</h2>
                  <p className="text-purple-400 verdana-regular">@{profileData.username || user?.username || 'usuario'}</p>
                  <p className="text-white/60 verdana-regular text-sm mt-1">{user?.email}</p>
                  <div className="flex items-center justify-center md:justify-start gap-2 mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs ${user?.user_type === 'author' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                      {user?.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs ${user?.membership_status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                      {user?.membership_status === 'active' ? 'Membresía Activa' : 'Pendiente'}
                    </span>
                  </div>
                </div>

                {/* Badges */}
                {badges.length > 0 && (
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-white/60 text-xs flex items-center gap-1">
                      <Award className="w-4 h-4" /> Insignias
                    </span>
                    <div className="flex gap-2">
                      {badges.map((badge, idx) => (
                        <div 
                          key={idx}
                          className="px-3 py-1.5 rounded-full flex items-center gap-1"
                          style={{ backgroundColor: `${badge.color}20`, border: `1px solid ${badge.color}50` }}
                          title={badge.description}
                        >
                          <span>{badge.icon}</span>
                          <span className="text-white text-xs">{badge.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sponsor Info - Mostrar si el usuario fue referido */}
              {profileData.sponsor && (
                <div className="mt-4 pt-4 border-t border-purple-500/20">
                  <div className="flex items-center gap-3 px-4">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center overflow-hidden">
                      {profileData.sponsor.profile_photo ? (
                        <img 
                          src={profileData.sponsor.profile_photo} 
                          alt={profileData.sponsor.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-5 h-5 text-purple-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-white/60 text-xs">Referido por:</p>
                      <p className="text-white font-semibold">{profileData.sponsor.name}</p>
                      <p className="text-purple-400 text-xs">@{profileData.sponsor.username}</p>
                    </div>
                    <span className={`ml-auto px-2 py-1 rounded-full text-xs ${
                      profileData.sponsor.user_type === 'author' 
                        ? 'bg-purple-500/20 text-purple-300' 
                        : 'bg-blue-500/20 text-blue-300'
                    }`}>
                      {profileData.sponsor.user_type === 'author' ? 'Autor' : 'Audio-Lector'}
                    </span>
                  </div>
                </div>
              )}

              {/* Enlace Único - Acceso directo */}
              <div className="mt-4 pt-4 border-t border-purple-500/20">
                <div className="flex flex-col sm:flex-row items-center gap-3 px-4">
                  <div className="flex-1">
                    <p className="text-white/60 text-xs mb-1">Tu Enlace Único</p>
                    <code className="text-violet-400 text-sm">{window.location.origin}/{profileData.username}</code>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-violet-500/50 text-violet-400 hover:bg-violet-500/20"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/${profileData.username}`);
                        toast.success('Enlace copiado');
                      }}
                    >
                      <Link className="w-4 h-4 mr-1" />
                      Copiar
                    </Button>
                    <Button
                      size="sm"
                      className="bg-violet-600 hover:bg-violet-700"
                      onClick={() => window.open(`/${profileData.username}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Ver Página
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                      onClick={() => navigate('/dashboard?tab=marketing')}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  </div>
                </div>

                {/* Redirección dinámica del enlace único */}
                <div className="mt-4 px-4" data-testid="referral-redirect-section">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="referral-redirect-toggle"
                      checked={!!profileData.referral_redirect_enabled}
                      onChange={(e) => setProfileData({ ...profileData, referral_redirect_enabled: e.target.checked })}
                      className="accent-violet-500 w-4 h-4"
                    />
                    <span className="text-white text-sm gotham-medium">Redirigir mi enlace único a otro destino</span>
                  </label>
                  <p className="text-white/50 text-xs mt-1 ml-6">
                    Al activarlo, quien visite <code className="text-violet-400">{window.location.origin}/{profileData.username}</code> será enviado automáticamente a la URL que definas (tu referido se conserva).
                  </p>
                  {profileData.referral_redirect_enabled && (
                    <div className="mt-2 ml-6 flex flex-col sm:flex-row gap-2">
                      <input
                        type="url"
                        data-testid="referral-redirect-url-input"
                        value={profileData.referral_redirect_url || ''}
                        onChange={(e) => setProfileData({ ...profileData, referral_redirect_url: e.target.value })}
                        placeholder="https://tu-destino.com"
                        className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-violet-500/30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                      <Button
                        size="sm"
                        data-testid="referral-redirect-save"
                        className="bg-violet-600 hover:bg-violet-700"
                        onClick={handleSaveProfile}
                      >
                        Guardar redirección
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { id: 'personal', label: 'Información Personal', icon: User },
              // Tab de Networking solo para autores
              ...(user?.user_type === 'author' ? [{ id: 'networking', label: 'Networking', icon: MessageCircle }] : []),
              // Tab de Mis Obras solo para autores
              ...(user?.user_type === 'author' ? [{ id: 'works', label: 'Mis Obras', icon: Book }] : []),
              { id: 'security', label: 'Seguridad', icon: Eye },
              { id: 'billing', label: 'Facturación', icon: CreditCard },
              { id: 'fiscal', label: 'Datos Fiscales', icon: FileText },
              { id: 'danger', label: 'Zona de Peligro', icon: AlertTriangle, danger: true },
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`profile-tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'danger' && !deletePreview) {
                    fetchDeletePreview();
                  }
                  if (tab.id === 'billing' && !billingHistory) {
                    fetchBillingHistory();
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg verdana-regular text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id 
                    ? tab.danger ? 'bg-red-600 text-white' : 'bg-purple-500 text-white' 
                    : tab.danger ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <Card className="card-viubux-dark">
            <CardContent className="pt-6">
              
              {/* Personal Info Tab */}
              {activeTab === 'personal' && (
                <div className="space-y-6">
                  <h3 className="gotham-bold text-xl text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-400" />
                    Información Personal
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Nombre Completo *</label>
                      <input
                        type="text"
                        value={profileData.full_name}
                        onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="Tu nombre completo"
                      />
                      <p className="text-white/40 text-xs mt-1">Obligatorio para el cobro de comisiones</p>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Nombre de Usuario</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400">@</span>
                        <input
                          type="text"
                          value={profileData.username}
                          onChange={(e) => setProfileData({...profileData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '')})}
                          className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-8 pr-4 py-3 text-white focus:outline-none focus:border-purple-500"
                          placeholder="tu-usuario"
                        />
                      </div>
                      <p className="text-white/40 text-xs mt-1">connect.viubux.com/{profileData.username || 'tu-usuario'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Teléfono</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                          className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-purple-500"
                          placeholder="+52 55 1234 5678"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <input
                          type="email"
                          value={user?.email || ''}
                          disabled
                          className="w-full bg-black/20 border border-white/10 rounded-lg pl-12 pr-4 py-3 text-white/50 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Correo personal adicional para iniciar sesión */}
                  <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-5" data-testid="secondary-email-section">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="w-4 h-4 text-purple-400" />
                      <h4 className="gotham-bold text-white text-sm">Correo personal adicional</h4>
                    </div>
                    <p className="verdana-regular text-xs text-white/50 mb-4">
                      Agrega un segundo correo (tu correo personal) para poder iniciar sesión con cualquiera de los dos. Te enviaremos un correo de confirmación antes de activarlo.
                    </p>
                    {secEmailCurrent ? (
                      <div className="flex items-center justify-between gap-3 bg-black/20 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                          <span className="verdana-regular text-sm text-white truncate" data-testid="secondary-email-current">{secEmailCurrent}</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={removeSecondaryEmail} disabled={secSaving}
                          data-testid="secondary-email-remove"
                          className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 text-xs shrink-0">
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar
                        </Button>
                      </div>
                    ) : secPending ? (
                      <div className="space-y-3" data-testid="secondary-email-pending">
                        <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3">
                          <Mail className="w-4 h-4 text-yellow-400 shrink-0" />
                          <span className="verdana-regular text-sm text-yellow-200/90">
                            Enviamos un correo de confirmación a <strong>{secPending}</strong>. Ábrelo y confirma para activarlo.
                          </span>
                        </div>
                        <Button variant="outline" size="sm" onClick={removeSecondaryEmail} disabled={secSaving}
                          className="border-white/20 text-white/70 hover:bg-white/10 h-8 text-xs">
                          Cancelar solicitud
                        </Button>
                      </div>
                    ) : (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input
                          type="email"
                          value={secEmail}
                          onChange={(e) => setSecEmail(e.target.value)}
                          placeholder="tucorreo.personal@gmail.com"
                          data-testid="secondary-email-input"
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <input
                          type="password"
                          value={secPassword}
                          onChange={(e) => setSecPassword(e.target.value)}
                          placeholder="Tu contraseña actual"
                          data-testid="secondary-email-password"
                          className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <div className="sm:col-span-2 flex justify-end">
                          <Button onClick={saveSecondaryEmail} disabled={secSaving}
                            data-testid="secondary-email-save"
                            className="bg-purple-500 hover:bg-purple-600 text-white h-9 text-sm">
                            {secSaving ? 'Guardando...' : 'Agregar correo adicional'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="bg-purple-500 hover:bg-purple-600 text-white flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Networking Tab */}
              {/* Community Networking Tab - Solo para Autores */}
              {activeTab === 'networking' && user?.user_type === 'author' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="gotham-bold text-xl text-white flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-purple-400" />
                        Perfil de Networking
                      </h3>
                      <p className="text-white/60 verdana-regular text-sm">
                        Esta información será visible para otros miembros de la comunidad ViubuxConnect cuando actives tu perfil público.
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowProfileGuide(true)}
                      variant="outline"
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                      size="sm"
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      Guía de Perfil Exitoso
                    </Button>
                  </div>

                  {/* Notice about profile visibility */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Eye className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-blue-400 gotham-bold text-sm mb-1">Visibilidad de tu perfil</p>
                        <p className="text-white/70 verdana-regular text-sm">
                          Para que otros miembros puedan encontrarte en la Zona de Networking, 
                          debes activar tu perfil público desde la página de <a href="/networking" className="text-purple-400 underline hover:text-purple-300">Comunidad</a>.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Bio
                      </label>
                      <textarea
                        value={profileData.bio}
                        onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 min-h-[100px]"
                        placeholder="Cuéntanos sobre ti..."
                        maxLength={500}
                      />
                      <p className="text-white/40 text-xs mt-1">{profileData.bio?.length || 0}/500 caracteres</p>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
                        <Target className="w-4 h-4" /> Mi Valor Agregado es:
                      </label>
                      <input
                        type="text"
                        value={profileData.value_proposition}
                        onChange={(e) => setProfileData({...profileData, value_proposition: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="¿Qué te hace único? ¿Qué puedes aportar a la comunidad?"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
                        <Heart className="w-4 h-4" /> Cuál es mi misión de vida:
                      </label>
                      <input
                        type="text"
                        value={profileData.life_mission}
                        onChange={(e) => setProfileData({...profileData, life_mission: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="¿Cuál es tu propósito?"
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
                          <Briefcase className="w-4 h-4" /> Soy / Mi Profesión es:
                        </label>
                        <input
                          type="text"
                          value={profileData.profession}
                          onChange={(e) => setProfileData({...profileData, profession: e.target.value})}
                          className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                          placeholder="Ej: Coach de vida, Escritor, Emprendedor..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
                          <Percent className="w-4 h-4" /> Descuento que ofrezco a la comunidad:
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={profileData.community_discount}
                            onChange={(e) => setProfileData({...profileData, community_discount: parseInt(e.target.value) || 0})}
                            className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 pr-12 text-white focus:outline-none focus:border-purple-500"
                            placeholder="0"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40">%</span>
                        </div>
                      </div>
                    </div>

                    {/* NEW: Mi Beneficio para la Comunidad */}
                    <div className="bg-green-500/5 border border-green-500/30 rounded-lg p-4">
                      <label className="block text-green-400 text-sm mb-2 flex items-center gap-2 gotham-bold">
                        <Gift className="w-4 h-4" /> Mi Beneficio para la Comunidad ViubuxConnect
                      </label>
                      <textarea
                        value={profileData.community_benefit}
                        onChange={(e) => setProfileData({...profileData, community_benefit: e.target.value})}
                        className="w-full bg-black/30 border border-green-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500 min-h-[80px]"
                        placeholder='Ej: "Ofrezco 10% de descuento en asesorías legales a miembros de ViubuxConnect" o "15 minutos de consultoría gratuita para miembros Connect"'
                        maxLength={300}
                      />
                      <p className="text-white/40 text-xs mt-1">
                        {profileData.community_benefit?.length || 0}/300 caracteres • Este campo ayuda a generar conexiones de valor
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" /> WhatsApp (para conectar):
                      </label>
                      <input
                        type="tel"
                        value={profileData.whatsapp}
                        onChange={(e) => setProfileData({...profileData, whatsapp: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="+52 55 1234 5678"
                      />
                    </div>
                    
                    <div className="pt-4">
                      <label className="block text-white/70 text-sm mb-3">Redes Sociales</label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="relative">
                          <Linkedin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
                          <input
                            type="url"
                            value={profileData.linkedin}
                            onChange={(e) => setProfileData({...profileData, linkedin: e.target.value})}
                            className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-purple-500"
                            placeholder="linkedin.com/in/tu-perfil"
                          />
                        </div>
                        
                        <div className="relative">
                          <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-400" />
                          <input
                            type="text"
                            value={profileData.instagram}
                            onChange={(e) => setProfileData({...profileData, instagram: e.target.value})}
                            className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-purple-500"
                            placeholder="@tu_instagram"
                          />
                        </div>
                        
                        <div className="relative">
                          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                          </svg>
                          <input
                            type="text"
                            value={profileData.tiktok}
                            onChange={(e) => setProfileData({...profileData, tiktok: e.target.value})}
                            className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-purple-500"
                            placeholder="@tu_tiktok"
                          />
                        </div>
                        
                        <div className="relative">
                          <Facebook className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                          <input
                            type="url"
                            value={profileData.facebook}
                            onChange={(e) => setProfileData({...profileData, facebook: e.target.value})}
                            className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-purple-500"
                            placeholder="facebook.com/tu-perfil"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="bg-purple-500 hover:bg-purple-600 text-white flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h3 className="gotham-bold text-xl text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-purple-400" />
                    Cambiar Contraseña
                  </h3>
                  
                  <div className="max-w-md space-y-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Contraseña Actual</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={passwordData.current_password}
                          onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                          className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 pr-12 text-white focus:outline-none focus:border-purple-500"
                          placeholder="Tu contraseña actual"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Nueva Contraseña</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                          className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 pr-12 text-white focus:outline-none focus:border-purple-500"
                          placeholder="Mínimo 6 caracteres"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Confirmar Nueva Contraseña</label>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="Repite la nueva contraseña"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-start pt-4">
                    <Button 
                      onClick={handleChangePassword}
                      disabled={saving || !passwordData.current_password || !passwordData.new_password}
                      className="bg-purple-500 hover:bg-purple-600 text-white flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Actualizando...' : 'Actualizar Contraseña'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Fiscal Tab */}
              {activeTab === 'fiscal' && (
                <div className="space-y-6">
                  <h3 className="gotham-bold text-xl text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    Datos Fiscales
                  </h3>
                  <p className="text-white/60 verdana-regular text-sm">
                    Requerido para el pago de tus regalías y comisiones
                  </p>
                  
                  <div className="max-w-md space-y-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">RFC</label>
                      <input
                        type="text"
                        value={fiscalData.rfc}
                        onChange={(e) => setFiscalData({...fiscalData, rfc: e.target.value.toUpperCase()})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 uppercase"
                        placeholder="XAXX010101000"
                        maxLength={13}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Razón Social / Nombre Fiscal</label>
                      <input
                        type="text"
                        value={fiscalData.razon_social}
                        onChange={(e) => setFiscalData({...fiscalData, razon_social: e.target.value})}
                        className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                        placeholder="Nombre o Razón Social"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        Constancia de Situación Fiscal (CSF)
                      </label>
                      <div 
                        className="border-2 border-dashed border-purple-500/30 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500/50 transition-colors"
                        onClick={() => document.getElementById('csf-input').click()}
                      >
                        <Upload className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        {fiscalData.csf_filename ? (
                          <p className="text-white verdana-regular text-sm">{fiscalData.csf_filename}</p>
                        ) : (
                          <>
                            <p className="text-white/70 verdana-regular text-sm">Haz clic para subir tu CSF</p>
                            <p className="text-white/40 text-xs mt-1">PDF o imagen (máx. 5MB)</p>
                          </>
                        )}
                      </div>
                      <input 
                        id="csf-input"
                        type="file" 
                        accept=".pdf,.jpg,.jpeg,.png" 
                        className="hidden" 
                        onChange={handleCSFUpload}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-start pt-4">
                    <Button 
                      onClick={handleSaveFiscalData}
                      disabled={saving}
                      className="bg-purple-500 hover:bg-purple-600 text-white flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Guardando...' : 'Guardar Datos Fiscales'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Facturación / Historial de Compras Tab */}
              {activeTab === 'billing' && (
                <div className="space-y-6" data-testid="billing-tab-content">
                  <div>
                    <h3 className="gotham-bold text-xl text-white flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-purple-400" />
                      Historial de Compras y Suscripciones
                    </h3>
                    <p className="verdana-regular text-sm text-white/60 mt-1">
                      Aquí puedes ver todas tus compras y suscripciones pagadas en ViubuxConnect.
                    </p>
                  </div>

                  {billingLoading ? (
                    <div className="flex items-center justify-center py-12" data-testid="billing-loading">
                      <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Detalle de Suscripción Activa */}
                      {subInfo && (() => {
                        const statusLabels = {
                          active: 'Activa', free: 'Gratuita', grace_period: 'Período de gracia',
                          paused: 'Pausada', expired: 'Expirada', pending: 'Pendiente', inactive: 'Inactiva',
                        };
                        const isActive = ['active', 'free', 'grace_period'].includes(subInfo.membership_status);
                        const expDate = subInfo.subscription_expires_at
                          ? new Date(subInfo.subscription_expires_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
                          : null;
                        return (
                          <div
                            data-testid="subscription-detail-card"
                            className={`rounded-xl p-5 border ${isActive ? 'bg-purple-500/10 border-purple-500/30' : 'bg-white/5 border-white/10'}`}
                          >
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                  <Calendar className="w-5 h-5 text-purple-300" />
                                </div>
                                <div>
                                  <p className="verdana-regular text-xs text-white/50">Estado de tu suscripción</p>
                                  <p className="gotham-bold text-lg text-white" data-testid="subscription-status-label">
                                    {statusLabels[subInfo.membership_status] || subInfo.membership_status || '—'}
                                  </p>
                                </div>
                              </div>
                              {subInfo.has_active_stripe_subscription ? (
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs verdana-regular bg-green-500/15 text-green-400 border border-green-500/30">
                                  <CheckCircle className="w-3.5 h-3.5" /> Renovación automática
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs verdana-regular bg-white/10 text-white/60 border border-white/20">
                                  Renovación manual
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                              <div className="bg-black/20 rounded-lg p-3">
                                <p className="verdana-regular text-xs text-white/50">
                                  {subInfo.has_active_stripe_subscription ? 'Próxima renovación' : 'Fecha de vencimiento'}
                                </p>
                                <p className="verdana-regular text-sm text-white font-medium mt-1" data-testid="subscription-expiry-date">
                                  {expDate || 'Sin suscripción activa'}
                                </p>
                              </div>
                              <div className="bg-black/20 rounded-lg p-3">
                                <p className="verdana-regular text-xs text-white/50">Días restantes</p>
                                <p className="verdana-regular text-sm text-white font-medium mt-1" data-testid="subscription-days-remaining">
                                  {expDate ? `${subInfo.days_remaining} día${subInfo.days_remaining === 1 ? '' : 's'}` : '—'}
                                </p>
                              </div>
                            </div>

                            {subInfo.countdown_message && (
                              <p className="verdana-regular text-sm text-yellow-400 mt-3" data-testid="subscription-countdown">
                                {subInfo.countdown_message}
                              </p>
                            )}
                            {subInfo.is_in_grace_period && !subInfo.countdown_message && (
                              <p className="verdana-regular text-sm text-yellow-400 mt-3">
                                ⚠️ Período de gracia: quedan {subInfo.grace_days_remaining} día(s) antes de pausar tu membresía.
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Resumen */}
                      {billingHistory && billingHistory.total_count > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid="billing-total-count">
                            <p className="verdana-regular text-xs text-white/50">Total de transacciones</p>
                            <p className="gotham-bold text-2xl text-white">{billingHistory.total_count}</p>
                          </div>
                          <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid="billing-total-paid">
                            <p className="verdana-regular text-xs text-white/50">Total pagado (MXN)</p>
                            <p className="gotham-bold text-2xl text-green-400">
                              ${(billingHistory.total_paid_mxn || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Lista */}
                      {billingHistory && billingHistory.history.length > 0 ? (
                        <div className="space-y-3" data-testid="billing-history-list">
                          {billingHistory.history.map((item) => {
                            const statusStyles = {
                              paid: 'bg-green-500/15 text-green-400 border-green-500/30',
                              pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
                              failed: 'bg-red-500/15 text-red-400 border-red-500/30',
                              refunded: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
                            };
                            const statusLabel = {
                              paid: 'Pagado', pending: 'Pendiente', failed: 'Rechazado', refunded: 'Reembolsado',
                            };
                            const date = item.created_at
                              ? new Date(item.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
                              : '—';
                            return (
                              <div
                                key={item.id}
                                data-testid={`billing-item-${item.id}`}
                                className="flex items-center justify-between gap-4 bg-white/5 hover:bg-white/10 transition-colors rounded-xl p-4 border border-white/10"
                              >
                                <div className="flex items-start gap-3 min-w-0">
                                  <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                                    <Receipt className="w-5 h-5 text-purple-400" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="verdana-regular text-sm text-white font-medium truncate">{item.description}</p>
                                    <div className="flex items-center gap-2 text-xs text-white/50 mt-1 flex-wrap">
                                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>
                                      {item.provider && item.provider !== '—' && (
                                        <span className="capitalize">· {item.provider}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="gotham-bold text-sm text-white">
                                    ${(item.amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} {item.currency}
                                  </p>
                                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] verdana-regular border ${statusStyles[item.status] || 'bg-white/10 text-white/60 border-white/20'}`}>
                                    {statusLabel[item.status] || item.status}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-12" data-testid="billing-empty">
                          <Receipt className="w-12 h-12 text-white/20 mx-auto mb-3" />
                          <p className="verdana-regular text-white/60">Aún no tienes compras ni suscripciones registradas.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Mis Obras Tab - Solo para Autores */}
              {activeTab === 'works' && user?.user_type === 'author' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="gotham-bold text-xl text-white flex items-center gap-2">
                      <Book className="w-5 h-5 text-purple-400" />
                      Mis Obras en Perfil Público
                    </h3>
                    <p className="text-white/60 verdana-regular text-sm mt-1">
                      Selecciona cuáles de tus obras registradas deseas mostrar en tu perfil público de networking.
                      Registra nuevas obras en la sección <strong className="text-purple-400">Regalías → Mis Obras</strong>.
                    </p>
                  </div>

                  {/* Lista de obras del sistema de regalías */}
                  {authorWorks.length === 0 ? (
                    <div className="text-center py-8">
                      <Book className="w-12 h-12 text-white/30 mx-auto mb-3" />
                      <p className="text-white/60">No tienes obras registradas</p>
                      <p className="text-white/40 text-sm mt-1">
                        Ve a <strong className="text-purple-400">Regalías → Mis Obras</strong> para registrar tus obras
                      </p>
                      <Button
                        onClick={() => navigate('/royalties')}
                        className="mt-4 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ir a Registrar Obras
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Botón para registrar nueva obra */}
                      <div className="flex justify-end mb-4">
                        <Button
                          onClick={() => navigate('/royalties')}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Registrar Nueva Obra
                        </Button>
                      </div>
                      {authorWorks.map((work) => (
                        <WorkProfileCard 
                          key={work.id}
                          work={work}
                          onToggleVisibility={toggleWorkVisibility}
                          onUpdateInfo={updateWorkPublicInfo}
                          saving={saving}
                        />
                      ))}
                    </div>
                  )}

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-blue-300 text-sm flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Las obras marcadas con "Mostrar en Perfil" aparecerán en tu perfil público de la zona de Networking.
                    </p>
                  </div>
                </div>
              )}

              {/* Danger Zone Tab */}
              {activeTab === 'danger' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h3 className="gotham-bold text-xl text-red-400">Zona de Peligro</h3>
                      <p className="text-white/60 text-sm">Acciones irreversibles para tu cuenta</p>
                    </div>
                  </div>

                  {/* Warning Banner */}
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 gotham-bold text-sm mb-1">Advertencia</p>
                        <p className="text-white/70 verdana-regular text-sm">
                          Las acciones en esta sección son <strong className="text-red-400">permanentes e irreversibles</strong>. 
                          Una vez eliminada tu cuenta, no podrás recuperarla.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Delete Account Section */}
                  <div className="border border-red-500/30 rounded-lg overflow-hidden">
                    <div className="bg-red-500/5 p-4 border-b border-red-500/20">
                      <h4 className="gotham-bold text-white flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-red-400" />
                        Eliminar Cuenta
                      </h4>
                    </div>
                    
                    <div className="p-4 space-y-4">
                      {/* Loading Preview */}
                      {loadingPreview && (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 text-red-400 animate-spin" />
                          <span className="ml-2 text-white/60">Cargando información...</span>
                        </div>
                      )}

                      {/* Preview Data */}
                      {deletePreview && !loadingPreview && (
                        <>
                          {/* Author Warning */}
                          {deletePreview.is_author && (
                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                              <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-yellow-400 gotham-bold text-sm mb-1">
                                    Aviso importante para Autores
                                  </p>
                                  <p className="text-white/70 verdana-regular text-sm">
                                    Al borrar tus datos personales, <strong className="text-yellow-300">podría desvincularse tu obra y los reportes de ventas asociados</strong>. 
                                    Tus obras permanecerán en el sistema pero sin autor asignado.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Warnings List */}
                          {deletePreview.warnings?.length > 0 && (
                            <div className="space-y-2 mb-4">
                              {deletePreview.warnings.map((warning, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm">
                                  <Info className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-white/70">{warning}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Data Summary */}
                          <div className="bg-black/30 rounded-lg p-4">
                            <p className="text-white/60 text-sm mb-3">Datos que serán eliminados:</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {deletePreview.data_to_delete?.works_to_orphan > 0 && (
                                <div className="bg-red-500/10 rounded p-2 text-center">
                                  <p className="text-red-400 gotham-bold text-lg">{deletePreview.data_to_delete.works_to_orphan}</p>
                                  <p className="text-white/50 text-xs">Obras</p>
                                </div>
                              )}
                              {deletePreview.data_to_delete?.commissions_earned > 0 && (
                                <div className="bg-red-500/10 rounded p-2 text-center">
                                  <p className="text-red-400 gotham-bold text-lg">{deletePreview.data_to_delete.commissions_earned}</p>
                                  <p className="text-white/50 text-xs">Comisiones</p>
                                </div>
                              )}
                              {deletePreview.data_to_delete?.referrals > 0 && (
                                <div className="bg-red-500/10 rounded p-2 text-center">
                                  <p className="text-red-400 gotham-bold text-lg">{deletePreview.data_to_delete.referrals}</p>
                                  <p className="text-white/50 text-xs">Referidos</p>
                                </div>
                              )}
                              {deletePreview.data_to_delete?.payment_transactions > 0 && (
                                <div className="bg-red-500/10 rounded p-2 text-center">
                                  <p className="text-red-400 gotham-bold text-lg">{deletePreview.data_to_delete.payment_transactions}</p>
                                  <p className="text-white/50 text-xs">Transacciones</p>
                                </div>
                              )}
                              {deletePreview.data_to_delete?.messages > 0 && (
                                <div className="bg-red-500/10 rounded p-2 text-center">
                                  <p className="text-red-400 gotham-bold text-lg">{deletePreview.data_to_delete.messages}</p>
                                  <p className="text-white/50 text-xs">Mensajes</p>
                                </div>
                              )}
                              {deletePreview.data_to_delete?.notifications > 0 && (
                                <div className="bg-red-500/10 rounded p-2 text-center">
                                  <p className="text-red-400 gotham-bold text-lg">{deletePreview.data_to_delete.notifications}</p>
                                  <p className="text-white/50 text-xs">Notificaciones</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Delete Button */}
                          <div className="pt-4 border-t border-red-500/20">
                            <Button
                              onClick={() => setShowDeleteModal(true)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid="delete-account-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar mi cuenta permanentemente
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
        </div>
      </div>

      {/* Profile Guide Modal */}
      {showProfileGuide && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-8 h-8 text-yellow-400" />
                  <div>
                    <h2 className="gotham-bold text-xl text-white">Guía: Tu Perfil de Networking en 3 Pasos</h2>
                    <p className="text-white/60 text-sm">Transforma tu perfil en una máquina de generar contactos</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowProfileGuide(false)}
                  className="text-white/60 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Step 1 */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white gotham-bold">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="gotham-bold text-white mb-2">El "Hook" (Anzuelo) de tu Propuesta de Valor</h3>
                    <p className="text-white/70 verdana-regular text-sm mb-3">
                      Olvida los títulos nobiliarios como "Escritor" o "Autor". La gente no busca autores, busca <span className="text-purple-400 font-semibold">resultados</span>. 
                      Tu primera frase debe responder a: <span className="text-white">¿Qué problema resuelves y para quién?</span>
                    </p>
                    <div className="grid md:grid-cols-2 gap-3 mt-3">
                      <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                        <p className="text-red-400 text-xs gotham-bold mb-1">❌ Error:</p>
                        <p className="text-white/60 text-sm">"Autor de 3 libros de finanzas personales."</p>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                        <p className="text-green-400 text-xs gotham-bold mb-1">✅ Acierto:</p>
                        <p className="text-white/60 text-sm">"Ayudo a padres jóvenes a jubilarse 10 años antes mediante estrategias de inversión."</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white gotham-bold">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="gotham-bold text-white mb-2">La Prueba de Poder (Credibilidad)</h3>
                    <p className="text-white/70 verdana-regular text-sm mb-3">
                      En un entorno digital, la confianza es la moneda de cambio. Si dices que eres experto, <span className="text-blue-400 font-semibold">demuéstralo</span> con un dato o un "entregable" tangible.
                    </p>
                    <ul className="space-y-2 text-white/70 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Portafolio dinámico:</strong> No solo menciones tu audiolibro; enlaza a una conferencia tuya, un artículo de blog o un caso de éxito.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">El "Micro-Logro":</strong> "He asesorado a más de 50 emprendedores en su estrategia de marca" suena mucho más potente que "Soy consultor de branding".</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white gotham-bold">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="gotham-bold text-white mb-2">El Cierre con "Beneficio de Comunidad"</h3>
                    <p className="text-white/70 verdana-regular text-sm mb-3">
                      Un perfil sin un <span className="text-green-400 font-semibold">Call to Action</span> (Llamada a la acción) es una oportunidad perdida. 
                      Debes decirle al usuario exactamente qué hacer después de leerte y por qué hacerlo ahora.
                    </p>
                    <div className="bg-black/30 rounded p-3 mt-2">
                      <p className="text-green-400 text-xs gotham-bold mb-1">💡 La Oferta Viubux:</p>
                      <p className="text-white/70 text-sm italic">"Si me contactas por aquí, te regalo una sesión de diagnóstico de 15 minutos o un 20% de descuento en mi primer taller."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Example Profile */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="gotham-bold text-white mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                  Ejemplo de un Perfil Ganador
                </h3>
                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-5">
                  <p className="text-purple-400 text-sm gotham-bold mb-2">Título: Estratega de Storytelling para Líderes</p>
                  <p className="text-white/80 verdana-regular text-sm mb-3">
                    <strong className="text-white">Propuesta de Valor:</strong> Transformo datos aburridos en narrativas emocionales que cierran ventas. 
                    Si tu equipo no sabe comunicar su visión, yo les enseño cómo hacerlo usando las estructuras de los grandes autores.
                  </p>
                  <div className="bg-green-500/20 border border-green-500/40 rounded p-3">
                    <p className="text-green-400 text-sm">
                      <strong>Mi Beneficio Viubux:</strong> 15% de descuento en mi programa "Pitch de Alto Impacto" exclusivo para miembros de la comunidad Connect.
                    </p>
                  </div>
                </div>
              </div>

              {/* Value Proposition Image */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-5 h-5 text-purple-400" />
                  <h3 className="gotham-bold text-white">Estructura de Propuesta de Valor</h3>
                </div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_connect-referral-1/artifacts/nb0pd4pz_IMG_0893.jpeg"
                  alt="Value Proposition Canvas"
                  className="w-full rounded-lg border border-white/10"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10">
              <Button
                onClick={() => setShowProfileGuide(false)}
                className="w-full btn-viubux"
              >
                ¡Entendido! Voy a mejorar mi perfil
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-red-500/50 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-red-500/30">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h2 className="gotham-bold text-xl text-red-400">Confirmar eliminación</h2>
                  <p className="text-white/60 text-sm">Esta acción no se puede deshacer</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Author Warning in Modal */}
              {deletePreview?.is_author && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-400 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Aviso:</strong> Al borrar tus datos personales, podría desvincularse tu obra y los reportes de ventas asociados.
                    </span>
                  </p>
                </div>
              )}

              <p className="text-white/70 text-sm">
                Para confirmar la eliminación permanente de tu cuenta, por favor:
              </p>

              {/* Password Input */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Ingresa tu contraseña</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full bg-black/30 border border-red-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                  placeholder="Tu contraseña actual"
                  data-testid="delete-password-input"
                />
              </div>

              {/* Confirmation Text Input */}
              <div>
                <label className="block text-white/70 text-sm mb-2">
                  Escribe <span className="text-red-400 font-mono">DELETE</span> para confirmar
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value.toUpperCase())}
                  className="w-full bg-black/30 border border-red-500/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 font-mono"
                  placeholder="DELETE"
                  data-testid="delete-confirmation-input"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-red-500/30 flex gap-3">
              <Button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteConfirmation('');
                }}
                variant="outline"
                className="flex-1 border-white/20 text-white/70 hover:bg-white/10"
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmation !== 'DELETE' || !deletePassword}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar cuenta
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
